import type { KpiGranularity, KpiKey, KpiMetrics, KpiPeriodBound } from '../kpi/kpi.interface';

/** What triggered a KPI (re)computation — a user's `GET` request, or the periodic refresh timer. */
export const KPI_COMPUTE_TRIGGER = { API: 'api', CRON: 'cron' } as const;
export type KpiComputeTrigger = (typeof KPI_COMPUTE_TRIGGER)[keyof typeof KPI_COMPUTE_TRIGGER];

/**
 * Service Bus payload for the `kpi-compute-request` topic.
 * Produced by `pharos-back-deliveries`'s `KpiService` (API-triggered) or `kpi-refresh-timer`
 * (cron-triggered), and consumed by `pharos-back-deliveries` itself (`KpiSbController`).
 */
export interface KpiComputeRequestPayload {
  from: KpiPeriodBound;
  granularity: KpiGranularity;
  key: KpiKey;
  siteId?: string;
  spaceId?: string;
  to: KpiPeriodBound;
  trigger: KpiComputeTrigger;
  /** Id of the requesting user — present for an `api` trigger, absent for a `cron` trigger (no single requester). */
  userId?: string;
}

/**
 * Service Bus payload for the `on-kpi-computed` topic.
 * Produced by `pharos-back-deliveries` once a KPI computation completes (or is found already
 * up to date) — for BOTH `api`- and `cron`-triggered requests. Consumed by `pharos-back-messenger`
 * to broadcast the result as a scope-routed `kpis:updated` resource event to every eligible
 * connection (not a single targeted user — see `RealtimeService.handleKpiComputed`).
 */
export interface KpiComputedPayload {
  from: KpiPeriodBound;
  granularity: KpiGranularity;
  key: KpiKey;
  kpiId: string;
  metrics: KpiMetrics;
  siteId?: string;
  spaceId?: string;
  to: KpiPeriodBound;
}

/** Payload for the `kpi-refresh-tick` topic — tenant-agnostic, no `identifiers.tenantId`. A tick whose `generation` doesn't match the current stored state is ignored. */
export interface KpiRefreshTickPayload {
  generation: number;
}
